import { useState, useMemo, useCallback } from 'react'
import { useRestaurantStore, getLocationForOrder } from '@restaurant/core'
import type { Order } from '@restaurant/core'
import type { AlertProps } from '@restaurant/core'

/** Format a phone number for display: +12095551234 → 209-555-1234 */
function formatPhoneDisplay(raw: string): string {
  const digits = raw.replace(/\D/g, '')
  const local = digits.length === 11 && digits.startsWith('1') ? digits.slice(1) : digits
  if (local.length === 10) {
    return `${local.slice(0, 3)}-${local.slice(3, 6)}-${local.slice(6)}`
  }
  return raw
}

interface ContactRestaurantAlert {
  /** Show the alert for the current location (no order context needed) */
  showForCurrentLocation: () => void
  /** Show the alert for a specific order's location (resolved via merchantId) */
  showForOrder: (order: Pick<Order, 'merchantId'>) => void
  /** Alert component props — spread onto <Alert /> */
  alertProps: Partial<AlertProps>
}

/**
 * Shared hook for the "Contact Restaurant" alert.
 * Shows the restaurant's phone number with mobile-aware actions:
 * - Mobile: "Call Now" (tel: link) + "Dismiss"
 * - Desktop: "Dismiss" only (user can see/copy the number)
 */
export function useContactRestaurantAlert(): ContactRestaurantAlert {
  const [visible, setVisible] = useState(false)
  const [phone, setPhone] = useState<string | null>(null)
  const [name, setName] = useState('')

  const { currentRestaurant, currentLocation } = useRestaurantStore()

  const isMobile = useMemo(() => {
    if (typeof window === 'undefined') return false
    return window.matchMedia('(pointer: coarse)').matches
  }, [])

  const dismiss = useCallback(() => setVisible(false), [])

  const showForCurrentLocation = useCallback(() => {
    setPhone(currentLocation?.phone || null)
    setName(currentRestaurant?.name || 'Restaurant')
    setVisible(true)
  }, [currentLocation, currentRestaurant])

  const showForOrder = useCallback(
    (order: Pick<Order, 'merchantId'>) => {
      if (!currentRestaurant) return
      const loc = getLocationForOrder(order, currentRestaurant, currentLocation ?? undefined)
      setPhone(loc.phone || null)
      setName(currentRestaurant.name)
      setVisible(true)
    },
    [currentRestaurant, currentLocation]
  )

  const alertProps: Partial<AlertProps> = useMemo(
    () => ({
      visible,
      title: 'Contact Restaurant',
      description: phone
        ? `Call ${name} at\n${formatPhoneDisplay(phone)}`
        : `Phone number not available for ${name}`,
      primaryAction:
        isMobile && phone
          ? {
              text: 'Call Now',
              onPress: () => {
                window.location.href = `tel:${phone}`
                dismiss()
              },
              testID: 'contact-alert-call',
            }
          : { text: 'Dismiss', onPress: dismiss, testID: 'contact-alert-dismiss' },
      secondaryAction:
        isMobile && phone
          ? { text: 'Dismiss', variant: 'Secondary' as const, onPress: dismiss, testID: 'contact-alert-dismiss' }
          : undefined,
      onBackgroundPress: dismiss,
      onEscapeKey: dismiss,
      testID: 'contact-restaurant-alert',
    }),
    [visible, phone, name, isMobile, dismiss]
  )

  return { showForCurrentLocation, showForOrder, alertProps }
}
